// src/pages/OrganizationSettingsPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import TimezoneSelect from "react-timezone-select";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getCurrentUser } from "../services/authService";
import { updateOrganizationSettings } from "../services/organizationService";
import { useTimezone } from "../contexts/TimezoneContext";
import BillingTab from "../components/BillingTab";
import RoomFieldsTab from "../components/RoomFieldsTab";
import CountrySelect from "../components/CountrySelect";
import OrganizationLogoManager from "../components/OrganizationLogoManager";
import { useDispatch, useSelector } from "react-redux";
import { updateOrganizationLogo, updateOrganizationTimezone, updateOrganizationSettings as updateOrgSettingsInRedux } from "../userSlice";

export default function OrganizationSettingsPage() {
  // short English comments only
  const [orgName, setOrgName] = useState("");
  const [timeZone, setTimeZone] = useState("UTC");
  const [startTime, setStartTime] = useState(dayjs("2025-01-01T08:00"));
  const [endTime, setEndTime] = useState(dayjs("2025-01-01T18:00"));
  const [currentTab, setCurrentTab] = useState(0);
  const [organizationId, setOrganizationId] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [companyAddress, setCompanyAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const { setOrgTimeZone } = useTimezone();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);

  // Tab configuration with anchors
  const tabs = useMemo(() => [
    { label: "General", anchor: "general" },
    { label: "Billing", anchor: "billing" },
    { label: "Room Fields", anchor: "room-fields" }
  ], []);

  // Get tab index from URL hash
  const getTabFromHash = useCallback((hash) => {
    const anchor = hash.replace('#', '');
    const tabIndex = tabs.findIndex(tab => tab.anchor === anchor);
    return tabIndex >= 0 ? tabIndex : 0;
  }, [tabs]);

  // Set URL hash from tab index
  const setHashFromTab = useCallback((tabIndex) => {
    const anchor = tabs[tabIndex]?.anchor || tabs[0].anchor;
    navigate(`#${anchor}`, { replace: true });
  }, [tabs, navigate]);

  // Handle URL hash changes and set initial tab
  useEffect(() => {
    const tabIndex = getTabFromHash(location.hash);
    setCurrentTab(tabIndex);
    
    // Set default hash if none exists
    if (!location.hash) {
      setHashFromTab(0);
    }
  }, [location.hash, getTabFromHash, setHashFromTab]);

  // Note: Removed automatic fetchCurrentUser() to prevent infinite loop
  // User data should already be loaded by the app's authentication system

  useEffect(() => {
    console.log('⚙️  OrganizationSettings: Loading data from Redux store...');
    console.log('⚙️  OrganizationSettings: Redux user:', user);
    
    if (user && user.currentOrganization) {
      console.log('⚙️  OrganizationSettings: Organization data from Redux:', {
        organization: user.currentOrganization,
        defaultStartTime: user.currentOrganization.defaultStartTime,
        defaultEndTime: user.currentOrganization.defaultEndTime
      });
      
      // Store the full organization object
      setOrganization(user.currentOrganization);
      
      // Set organization ID for billing tab
      setOrganizationId(user.currentOrganization._id);
      
      if (user.currentOrganization.name) setOrgName(user.currentOrganization.name);
      if (user.currentOrganization.timeZone)
        setTimeZone(user.currentOrganization.timeZone);

      // Load company address
      if (user.currentOrganization.companyAddress) {
        setCompanyAddress(user.currentOrganization.companyAddress);
      }

      if (user.currentOrganization.defaultStartTime) {
        const str = user.currentOrganization.defaultStartTime;
        console.log('⚙️  OrganizationSettings: Processing start time from Redux:', str);
        const dayjsObj = dayjs(`2025-01-01T${str}`);
        const finalStartTime = dayjsObj.isValid() ? dayjsObj : dayjs("2025-01-01T08:00");
        console.log('⚙️  OrganizationSettings: Setting start time to:', finalStartTime.format('HH:mm'));
        setStartTime(finalStartTime);
      } else {
        console.log('⚙️  OrganizationSettings: No defaultStartTime found in Redux, using 08:00');
        setStartTime(dayjs("2025-01-01T08:00"));
      }
      
      if (user.currentOrganization.defaultEndTime) {
        const str = user.currentOrganization.defaultEndTime;
        console.log('⚙️  OrganizationSettings: Processing end time from Redux:', str);
        const dayjsObj = dayjs(`2025-01-01T${str}`);
        const finalEndTime = dayjsObj.isValid() ? dayjsObj : dayjs("2025-01-01T18:00");
        console.log('⚙️  OrganizationSettings: Setting end time to:', finalEndTime.format('HH:mm'));
        setEndTime(finalEndTime);
      } else {
        console.log('⚙️  OrganizationSettings: No defaultEndTime found in Redux, using 18:00');
        setEndTime(dayjs("2025-01-01T18:00"));
      }
    } else {
      console.log('❌ OrganizationSettings: No user or organization found in Redux');
      if (!user) {
        enqueueSnackbar("Please log in to access organization settings", {
          variant: "error",
        });
      }
    }
  }, [user, enqueueSnackbar]);

  async function handleSave(e) {
    e.preventDefault();
    try {
      const formatStart = startTime.format("HH:mm");
      const formatEnd = endTime.format("HH:mm");
      
      console.log('💾 OrganizationSettings: Starting save process');
      console.log('💾 OrganizationSettings: Form values:', {
        name: orgName,
        timeZone,
        defaultStartTime: formatStart,
        defaultEndTime: formatEnd,
        startTime: startTime.toString(),
        endTime: endTime.toString()
      });

      const result = await updateOrganizationSettings({
        name: orgName,
        timeZone,
        defaultStartTime: formatStart,
        defaultEndTime: formatEnd,
        companyAddress: companyAddress,
      });
      
      console.log('💾 OrganizationSettings: Backend response:', result);
      
      // Update the timezone context with the new timezone
      setOrgTimeZone(timeZone);
      
      // Update the Redux store with all the new settings
      const settingsUpdate = {
        name: orgName,
        timeZone: timeZone,
        defaultStartTime: formatStart,
        defaultEndTime: formatEnd,
        companyAddress: companyAddress,
      };
      
      console.log('Updating Redux store with new organization settings:', settingsUpdate);
      dispatch(updateOrgSettingsInRedux(settingsUpdate));
      
      enqueueSnackbar(result.message || "Updated successfully", {
        variant: "success",
      });
    } catch (err) {
      if (err.response && err.response.data?.error) {
        enqueueSnackbar(err.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("Failed to update organization settings", {
          variant: "error",
        });
      }
    }
  }

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setHashFromTab(newValue);
  };

  const handleCompanyAddressChange = (e) => {
    const { name, value } = e.target;
    setCompanyAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpdate = (newLogo) => {
    setOrganization(prev => ({
      ...prev,
      logo: newLogo
    }));
    // Update Redux state so navbar shows the new logo immediately
    dispatch(updateOrganizationLogo(newLogo));
  };

  // General Settings Tab Component
  const GeneralSettingsTab = () => (
    <Box component="form" onSubmit={handleSave} sx={{ mt: 2 }}>
      <TextField
        label="Organization Name"
        variant="outlined"
        fullWidth
        sx={{ mb: 3 }}
        value={orgName}
        onChange={(e) => setOrgName(e.target.value)}
      />

      {/* Organization Logo Manager */}
      <Box sx={{ mb: 4 }}>
        <OrganizationLogoManager 
          organization={organization} 
          onLogoUpdate={handleLogoUpdate}
        />
      </Box>

      <Typography variant="body1" sx={{ mb: 1 }}>
        Time Zone
      </Typography>
      <Box sx={{ 
        mb: 3, 
        position: 'relative',
        zIndex: 10,
        '& .react-select__menu': {
          zIndex: 1000
        },
        '& .react-select__menu-portal': {
          zIndex: 1000
        }
      }}>
        <TimezoneSelect
          value={timeZone}
          onChange={(tz) => setTimeZone(tz.value)}
          styles={{
            menuPortal: (base) => ({ 
              ...base, 
              zIndex: 1000 
            }),
            menu: (base) => ({ 
              ...base, 
              zIndex: 1000 
            })
          }}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        />
      </Box>

      {/* Company Address Section */}
      <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
        Company Address
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            name="line1"
            value={companyAddress.line1}
            onChange={handleCompanyAddressChange}
            placeholder="Street address"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            name="line2"
            value={companyAddress.line2}
            onChange={handleCompanyAddressChange}
            placeholder="Apartment, suite, etc."
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="City"
            name="city"
            value={companyAddress.city}
            onChange={handleCompanyAddressChange}
            placeholder="City"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="State/Province"
            name="state"
            value={companyAddress.state}
            onChange={handleCompanyAddressChange}
            placeholder="State"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Postal Code"
            name="postal_code"
            value={companyAddress.postal_code}
            onChange={handleCompanyAddressChange}
            placeholder="ZIP/Postal code"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <CountrySelect
            value={companyAddress.country}
            onChange={(value) => setCompanyAddress(prev => ({ ...prev, country: value }))}
          />
        </Grid>
      </Grid>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          label="Default Start Time"
          value={startTime}
          onChange={(newValue) => {
            if (newValue) setStartTime(newValue);
          }}
          slots={{
            textField: TextField
          }}
          slotProps={{
            textField: {
              sx: { mb: 3 },
              fullWidth: true
            }
          }}
        />

        <TimePicker
          label="Default End Time"
          value={endTime}
          onChange={(newValue) => {
            if (newValue) setEndTime(newValue);
          }}
          slots={{
            textField: TextField
          }}
          slotProps={{
            textField: {
              sx: { mb: 3 },
              fullWidth: true
            }
          }}
        />
      </LocalizationProvider>

      <Box>
        <Button variant="contained" type="submit">
          Save
        </Button>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Organization Settings
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            {tabs.map((tab, index) => (
              <Tab key={tab.anchor} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        {currentTab === 0 && <GeneralSettingsTab />}
        {currentTab === 1 && (
          <BillingTab organizationId={organizationId} />
        )}
        {currentTab === 2 && <RoomFieldsTab />}
      </Paper>
    </Container>
  );
}
